import moment, { type Moment } from "moment/min/moment-with-locales";

/**
 * Event display helpers shared by the student feed, list, calendar and detail
 * screens. Dates are formatted with moment, matching the RN app; the
 * `zh_hant` i18n language is mapped onto moment's `zh-hk` locale so month and
 * weekday names are localised.
 */

export const MOMENT_LOCALES: Record<string, string> = {
  zh_hant: "zh-hk",
  en: "en",
};

/**
 * Pick the base (caption-language) value or its `*_en` twin according to the
 * app language. Scraped events keep the caption language in the base columns
 * and a faithful English version in the `*_en` ones, so English readers get
 * `valueEn` while `zh_hant` readers (and events with no English twin) get the
 * base value.
 */
export function localized(
  value: string | null | undefined,
  valueEn: string | null | undefined,
  language: string,
): string {
  if (language === "en" && valueEn) return valueEn;
  return value || "";
}

const DATE_FORMATS = ["YYYY/MM/DD", "YYYY-MM-DD", moment.ISO_8601];

const parseClockHour = (time: string): number => {
  const match = /^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/i.exec(time.trim());
  if (!match) return 23;
  let hour = Number(match[1]);
  const meridiem = match[3]?.toLowerCase();
  if (meridiem === "pm" && hour < 12) hour += 12;
  if (meridiem === "am" && hour === 12) hour = 0;
  return hour;
};

export const withMomentLocale = (language: string) => {
  moment.locale(MOMENT_LOCALES[language] ?? "en");
  return moment;
};

/** `"6:00 pm"` / `"18:00"` -> `"18:00"`, the shape of `<input type="time">`. */
export const toTwentyFourHourClock = (time?: string | null): string => {
  if (!time) return "";
  const match = /^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/i.exec(time.trim());
  if (!match) return "";
  if (!match[3]) {
    return `${String(Number(match[1])).padStart(2, "0")}:${match[2] ?? "00"}`;
  }
  return `${String(parseClockHour(time) % 12).padStart(2, "0")}:${match[2] ?? "00"}`;
};

/** `"18:00"` -> `"06:00 pm"`, the `hh:mm a` shape the RN app stores. */
export const toTwelveHourClock = (time?: string | null): string => {
  if (!time) return "";
  const match = /^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/i.exec(time.trim());
  if (!match) return "";
  if (match[3]) return time.trim();
  const hour = Number(match[1]);
  const meridiem = hour < 12 ? "am" : "pm";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(hour12).padStart(2, "0")}:${match[2] ?? "00"} ${meridiem}`;
};

/** Parse an event timestamp, tolerating the ISO and `YYYY/MM/DD` shapes. */
export const parseEventDate = (value?: string | null): Moment | null => {
  if (!value) return null;
  const parsed = /^\d{4}-\d{2}-\d{2}T/.test(value)
    ? moment(value)
    : moment(value, DATE_FORMATS);
  return parsed.isValid() ? parsed : null;
};

export type ContactEntry = { label: string; value: string };

/** Coerce a Prisma JSON column into a plain array. */
export const toArray = (value: unknown): any[] => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

/**
 * Collect the cover image URLs of an event, skipping blanks. Legacy rows store
 * `{ url }` (or `{ type, url }`) objects instead of plain strings, so both
 * shapes are accepted and mapped down to the URL.
 */
export const toImageList = (value: unknown): string[] =>
  toArray(value)
    .map((item) => {
      if (item && typeof item === "object") {
        const image = item as { url?: string };
        return String(image.url ?? "");
      }
      return String(item ?? "");
    })
    .filter((url) => url.length > 0);

/** Normalize a JSON column of `hku` / `cuhk` university codes. */
export const toUniversityCodes = (value: unknown): string[] =>
  toArray(value).filter(
    (item): item is string => typeof item === "string" && item.length > 0,
  );

/** Normalize a JSON column of plain `#tags`, dropping a leading `#`. */
export const toHashTags = (value: unknown): string[] =>
  toArray(value)
    .filter((item): item is string => typeof item === "string" && item.length > 0)
    .map((tag) => tag.replace(/^#/, ""));

/**
 * Normalize the `emails` / `phones` / `links` JSON columns, which mix plain
 * strings with `{ input1, input2 }` label/value pairs.
 */
export const toContactList = (
  value: unknown,
  fallbackLabel: string,
): ContactEntry[] =>
  toArray(value)
    .map((item) => {
      if (item && typeof item === "object") {
        const pair = item as { input1?: string; input2?: string };
        return { label: pair.input1 || fallbackLabel, value: String(pair.input2 ?? "") };
      }
      return { label: fallbackLabel, value: String(item ?? "") };
    })
    .filter((entry) => entry.value.length > 0);

/** `YYYY-MM-DD` day key of an event timestamp, in the viewer's local time. */
export const toDateKey = (value?: string | null): string | null =>
  parseEventDate(value)?.format("YYYY-MM-DD") ?? null;

export const formatDate = (value?: string | null, format = "YYYY/MM/DD"): string =>
  parseEventDate(value)?.format(format) ?? "";

/** How often an event recurs, as stored in the `repeat_type` enum. */
type RepeatCadence = "never" | "daily" | "weekly";

/** The event fields the calendar layout depends on. */
type RepeatDetails = {
  start_date?: string | null;
  end_date?: string | null;
  end_date_on?: string | null;
  repeat_type?: string | null;
  original_start_date?: string | null;
};

/** Normalize `repeat_type`; a missing or unrecognised value reads as `never`. */
const repeatCadence = (details: {
  repeat_type?: string | null;
}): RepeatCadence => {
  const value = String(details.repeat_type ?? "").trim().toLowerCase();
  return value === "daily" || value === "weekly" ? value : "never";
};

/**
 * One occurrence of a `daily` / `weekly` repeat, as emitted by the monthly
 * API: its repeat expansion stamps `original_start_date` onto every row it
 * splits, so the row's own `start_date` is a single occurrence day.
 */
const isExpandedOccurrence = (details: RepeatDetails): boolean =>
  repeatCadence(details) !== "never" && Boolean(details.original_start_date);

/**
 * A continuous run: a `never` event whose `end_date` falls after its
 * `start_date`, e.g. a promo or benefit window valid over a date range. The
 * calendar draws it as one band rather than a pill on every covered day.
 */
export const isMultiDayRun = (details: RepeatDetails): boolean => {
  if (repeatCadence(details) !== "never") return false;
  const start = parseEventDate(details.start_date);
  const end = parseEventDate(details.end_date);
  if (!start || !end) return false;
  return end
    .clone()
    .startOf("day")
    .isAfter(start.clone().startOf("day"));
};

/** How one event row covers a day grid: a single band, or a pill per day. */
export type EventDayLayout =
  | { kind: "banner"; startKey: string; endKey: string }
  | { kind: "pills"; keys: string[] };

/** Safety cap on how many days one repeat may be expanded into. */
const MAX_RANGE_DAYS = 2000;

/**
 * Decide how an event lays out on a calendar straight from its stored repeat
 * data, the way a native calendar reads it:
 *   - a `never` event whose `end_date` is after its `start_date` covers that
 *     whole run as one continuous band
 *   - a row the API already expanded for the month (a `daily` / `weekly` event
 *     carrying `original_start_date`) is a single occurrence: one pill
 *   - a `daily` / `weekly` event the API has not expanded yet covers every day
 *     (or every 7th day) from `start_date` through `end_date_on`
 *   - anything else — a single day, a missing or unparseable date, a reversed
 *     range — is a discrete pill on its start day
 *
 * Null means the row carries no usable start date and cannot be placed.
 */
export const eventDayLayout = (
  details: RepeatDetails,
): EventDayLayout | null => {
  const start = parseEventDate(details.start_date);
  if (!start) return null;
  const startKey = start.format("YYYY-MM-DD");
  const cadence = repeatCadence(details);

  if (cadence !== "never" && !isExpandedOccurrence(details)) {
    const endOn = parseEventDate(details.end_date_on);
    const step = cadence === "weekly" ? 7 : 1;
    const keys: string[] = [];
    if (endOn) {
      const cursor = start.clone();
      for (
        let day = 0;
        day < MAX_RANGE_DAYS && !cursor.isAfter(endOn);
        day += step
      ) {
        keys.push(cursor.format("YYYY-MM-DD"));
        cursor.add(step, "day");
      }
    }
    if (keys.length > 0) return { kind: "pills", keys };
    return { kind: "pills", keys: [startKey] };
  }

  if (isMultiDayRun(details)) {
    return {
      kind: "banner",
      startKey,
      endKey: (parseEventDate(details.end_date) as Moment).format("YYYY-MM-DD"),
    };
  }

  return { kind: "pills", keys: [startKey] };
};

/** `8月12日 – 10月15日` / `8/12 – 10/15`, the meta line of a continuous run. */
export const formatPeriodRange = (
  details: { start_date?: string | null; end_date?: string | null },
  language: string,
): string => {
  const startMoment = parseEventDate(details.start_date);
  const endMoment = parseEventDate(details.end_date);
  if (!startMoment || !endMoment) return "";
  const pattern = language === "en" ? "M/D" : "M月D日";
  return `${startMoment.format(pattern)} – ${endMoment.format(pattern)}`;
};

/** `2026/03/04` or `2026/03/04 - 2026/03/06` when the event spans days. */
export const formatDateRange = (start?: string | null, end?: string | null): string => {
  const startFormatted = formatDate(start);
  if (!startFormatted) return "";
  const endFormatted = formatDate(end);
  if (!endFormatted || endFormatted === startFormatted) return startFormatted;
  return `${startFormatted} - ${endFormatted}`;
};

/** `18:00` or `18:00 - 21:00`, mirroring the RN details screen. */
export const formatTimeRange = (start?: string | null, end?: string | null): string => {
  if (!start) return "";
  return end && end !== start ? `${start} - ${end}` : start;
};

/** `Application deadline - 2026/03/04 18:00` label. */
export const formatDeadline = (date?: string | null, time?: string | null): string => {
  const formattedDate = formatDate(date);
  if (!formattedDate) return "";
  return time ? `${formattedDate} ${time}` : formattedDate;
};

/** True once the event's end timestamp is in the past. */
export const hasEventEnded = (end?: string | null, endTime?: string | null): boolean => {
  const endMoment = parseEventDate(end);
  if (!endMoment) return false;
  return endTime
    ? endMoment.clone().set("hour", parseClockHour(endTime)).isBefore(moment())
    : endMoment.endOf("day").isBefore(moment());
};

/** `in 3 days` / `2 days ago` style label used on event cards. */
export const formatRelativeDay = (value?: string | null): string =>
  parseEventDate(value)?.fromNow() ?? "";

/**
 * The `post_type` values the API assigns, mapped to the i18n key of the badge
 * the student screens show for them. An unknown or missing value has no entry,
 * so its badge stays hidden.
 */
export const POST_TYPE_LABELS: Record<string, string> = {
  EVENT: "PostType.Event",
  SOCIETY_ACTIVITY: "PostType.SocietyActivity",
  RESULT: "PostType.Result",
  RECRUITMENT: "PostType.Recruitment",
  ANNOUNCEMENT: "PostType.Announcement",
  GENERAL_POST: "PostType.Post",
  OTHER: "PostType.Other",
};

/** The badge label key of an event's `post_type`, or undefined when unknown. */
export const postTypeLabelKey = (postType?: string | null): string | undefined =>
  postType ? POST_TYPE_LABELS[postType] : undefined;

/** Orange-on-cream pill shared by every `post_type` badge. */
export const POST_TYPE_BADGE_CLASS =
  "inline-flex items-center rounded-full bg-[#fff3e0] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#ffa217]";

const LOCALIZED_NAME_KEYS: Record<string, string> = {
  zh_hant: "name_zh_hant",
  en: "name_en",
};

/** Read `name_zh_hant` / `name_en` off a localized JSON record. */
export const pickLocalizedName = (
  value: unknown,
  language: string,
): string | null => {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const keys = [
    LOCALIZED_NAME_KEYS[language],
    "name_en",
    "name_zh_hant",
  ].filter(Boolean) as string[];
  for (const key of keys) {
    const found = record[key];
    if (typeof found === "string" && found.trim()) return found;
  }
  return null;
};
